import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useFpB2bSyncGiveawayValuesToForm } from '@cart-services/hooks/fp-b2b/use-fp-b2b-sync-giveaway-values-to-form'
import { type GiveawayValue, type RegularGiveawayValue } from '@cart-services/types'
import { type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { DIALOG_REDIRECT_CONFIRM, DialogRedirectConfirm, type DialogRedirectConfirmProps } from '@cart-ui/DialogRedirectConfirm/DialogRedirectConfirm'
import { useDialog } from '@dni-hooks/useDialog'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { Container } from '@pets-ui/Container/Container'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom-v5'
import { CartEmpty } from '../../../../campaign/cart/components/CartEmpty/CartEmpty'
import { CartAddonProducts } from '../CartAddonProducts/CartAddonProducts'
import { CartFooter } from '../CartFooter/CartFooter'
import { CartGiveaways } from '../CartGiveaways/CartGiveaways'
import { CartHeader } from '../CartHeader/CartHeader'
import { useCart } from '../CartProvider/CartProvider'
import { CartRegularGiveawayTips } from '../CartRegularGiveawayTips/CartRegularGiveawayTips'
import { CartListItems } from './CartListItems'

export const CartBasic = () => {
  const { openDialog: openDialogRedirectConfirm, closeDialog: closeDialogRedirectConfirm } = useDialog<DialogRedirectConfirmProps>(DIALOG_REDIRECT_CONFIRM)
  const { campaignCodeFromUrl, campaignId, projectCode, countryCode, deliveryClass, platform } = useCart()
  const { resetCartValues, isCartEmpty } = useFpB2bCartService(campaignId, platform)
  const { clearInvalidSelectedGiveaways, clearInvalidManualInputCouponIds } = useFpB2bCouponService(campaignId, projectCode, countryCode, campaignCodeFromUrl, platform)
  const { resetPaymentTransaction, checkIfNeedRedirectToOrderPayPage } = useFpB2bPaymentService({
    campaignId,
    campaignCode: campaignCodeFromUrl,
    platform,
  })
  const { setValue } = useFormContext<CartFormValues>()
  const history = useHistory()

  const handleDialogDisplay = () => openDialogRedirectConfirm({
    title: '訂單未完成結帳',
    description: '尚有待付款訂單，是否前往結帳？',
    confirmText: '前往結帳',
    cancelText: '返回首頁',
    onConfirm: () => {
      closeDialogRedirectConfirm()
      resetCartValues()
      resetPaymentTransaction()
      history.push(`/merchant/${campaignCodeFromUrl}/my-account/order?tabValue=pending`)
    },
    onCancel: () => {
      closeDialogRedirectConfirm()
      resetCartValues()
      resetPaymentTransaction()
      history.push(`/merchant/${campaignCodeFromUrl}/product-catalog`)
    },
  })

  useEffectOnce(() => {
    checkIfNeedRedirectToOrderPayPage(handleDialogDisplay)
  })

  const handleGiveawayValuesChange = (giveawayValues?: GiveawayValue[]) => {
    setValue('giveaways', giveawayValues)
  }

  const handleRegularGiveawayValuesChange = (regularGiveawayValues?: RegularGiveawayValue[]) => {
    setValue('regularGiveaways', regularGiveawayValues)
  }

  useFpB2bSyncGiveawayValuesToForm({
    campaignId,
    campaignCode: campaignCodeFromUrl,
    projectCode,
    countryCode,
    deliveryClass,
    onGiveawayValuesChange: handleGiveawayValuesChange,
    onRegularGiveawayValuesChange: handleRegularGiveawayValuesChange,
    platform,
  })

  useEffect(() => {
    clearInvalidSelectedGiveaways()
  }, [clearInvalidSelectedGiveaways])

  useEffect(() => {
    clearInvalidManualInputCouponIds()
  }, [clearInvalidManualInputCouponIds])

  return (
    <>
      <Container p="12px" maxWidth={950}>
        {isCartEmpty
          ? <CartEmpty environment="merchant" campaignCode={campaignCodeFromUrl} />
          : (
            <VStack gap={3}>
              <CartHeader />
              <CartListItems />
              <CartRegularGiveawayTips />
              <CartGiveaways />
              <CartAddonProducts />
              <DialogRedirectConfirm />
            </VStack>
          )}
      </Container>
      <CartFooter />
    </>
  )
}
